import { useState } from "react";
import { ChevronDown, ShoppingBag, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { TAG_STYLES } from "../data/products";
import { useCart } from "../context/CartContext";
import { cartKeyFor } from "../context/ProductsContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";

const LOW_STOCK = 5;

export const ProductCard = ({ product }) => {
  const sizes = product.sizes && product.sizes.length ? product.sizes : [{ size: "Regular", price: product.price }];
  const [openIng, setOpenIng] = useState(false);
  const [selSize, setSelSize] = useState(sizes[0].size);
  const [qty, setQty] = useState(1);
  const { addItem, items } = useCart();
  const from = Math.min(...sizes.map((p) => p.price));
  const key = cartKeyFor(product);

  // Stock: null = not tracked. Count what's already in the basket against it.
  const stock = product.quantity;
  const inBasket = items.filter((i) => i.id === key).reduce((n, i) => n + i.qty, 0);
  const left = stock === null || stock === undefined ? Infinity : Math.max(0, stock - inBasket);
  const soldOut = stock === 0;
  const maxQty = Math.min(99, left);
  // If the admin renames/removes the selected size, fall back to the first one.
  const opt = sizes.find((p) => p.size === selSize) || sizes[0];

  const handleAdd = () => {
    if (qty > left) {
      toast.error(left === 0 ? `No more ${product.name} available right now.` : `Only ${left} more available.`);
      return;
    }
    addItem({ id: key, name: product.name, size: opt.size, price: opt.price }, qty);
    toast.success(`Added ${qty}x ${product.name} (${opt.size}) to basket`);
    setQty(1);
  };

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-3xl border border-[#EFE9E0] overflow-hidden flex flex-col transition-all duration-300 hover:shadow-[0_30px_60px_-35px_rgba(45,40,37,0.4)] hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={product.image_url}
          alt={product.name}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${soldOut ? "grayscale opacity-70" : ""}`}
        />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[85%]">
          {(product.tags || []).filter((t) => TAG_STYLES[t]).map((t) => (
            <span
              key={t}
              className="text-[10px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm"
              style={{ backgroundColor: TAG_STYLES[t].bg + "F2", color: TAG_STYLES[t].text }}
            >
              {TAG_STYLES[t].label}
            </span>
          ))}
        </div>
        {soldOut ? (
          <span data-testid={`sold-out-${product.id}`} className="absolute bottom-3 right-3 text-xs font-semibold px-3 py-1.5 rounded-full bg-ink/85 text-cream">
            Sold out
          </span>
        ) : stock != null && stock <= LOW_STOCK ? (
          <span data-testid={`low-stock-${product.id}`} className="absolute bottom-3 right-3 text-xs font-semibold px-3 py-1.5 rounded-full bg-terracotta text-white">
            Only {stock} left
          </span>
        ) : null}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-serif text-xl text-ink leading-snug">{product.name}</h3>
        <p className="mt-2 text-sm text-[#6E6760] leading-relaxed">{product.description}</p>

        {(product.net_carbs || product.sugar) && (
          <div className="mt-4 flex gap-3">
            {product.net_carbs && (
              <span className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#F3F7F1] text-[#3E5334]">
                {product.net_carbs} net carbs
              </span>
            )}
            {product.sugar && (
              <span className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#FDF5F0] text-[#A75429]">
                {product.sugar} sugar
              </span>
            )}
          </div>
        )}

        {(product.ingredients || []).length > 0 && (
        <Collapsible open={openIng} onOpenChange={setOpenIng} className="mt-4">
          <CollapsibleTrigger
            data-testid={`ingredients-toggle-${product.id}`}
            className="flex items-center gap-1.5 text-sm font-medium text-olive hover:text-olive-hover"
          >
            Nothing Hidden — Ingredients
            <ChevronDown size={16} className={`transition-transform ${openIng ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <ul className="flex flex-wrap gap-1.5">
              {product.ingredients.map((ing) => (
                <li key={ing} className="text-xs text-[#524B46] bg-[#F5F0EA] px-2.5 py-1 rounded-md">
                  {ing}
                </li>
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
        )}

        <div className="mt-auto pt-5 border-t border-[#EFE9E0] mt-5">
          <div className="flex items-end justify-between gap-3">
            <div>
              <span className="text-xs text-[#787069]">From</span>
              <p className="font-serif text-2xl text-ink leading-none">${from.toFixed(2)}</p>
            </div>
            <select
              value={opt.size}
              onChange={(e) => setSelSize(e.target.value)}
              data-testid={`size-select-${product.id}`}
              className="rounded-lg border border-[#E8E2D9] bg-cream text-sm px-3 py-2.5 text-ink max-w-[62%]"
            >
              {sizes.map((p) => (
                <option key={p.size} value={p.size}>
                  {p.size} — ${p.price.toFixed(2)}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button
                data-testid={`qty-minus-${product.id}`}
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="h-9 w-9 rounded-full border border-[#E8E2D9] flex items-center justify-center text-ink"
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>
              <span data-testid={`qty-value-${product.id}`} className="w-6 text-center text-sm font-semibold text-ink">{qty}</span>
              <button
                data-testid={`qty-plus-${product.id}`}
                onClick={() => setQty((q) => Math.min(Math.max(1, maxQty), q + 1))}
                className="h-9 w-9 rounded-full border border-[#E8E2D9] flex items-center justify-center text-ink"
                aria-label="Increase quantity"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              data-testid={`add-to-basket-${product.id}`}
              onClick={handleAdd}
              disabled={soldOut || left === 0}
              className="flex-1 h-11 rounded-full bg-olive hover:bg-olive-hover text-cream text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingBag size={16} /> {soldOut ? "Sold out" : left === 0 ? "All in basket" : "Add to Basket"}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};
